import { isDate, isValid, parseISO } from 'date-fns';
import { isOneOf } from '../../shared/utils/type-guards';
import {
  DATETIME_FILTER_UNITS,
  DATE_FILTER_ABSOLUTE_METHODS,
  DATE_FILTER_MODES,
  DATE_FILTER_RELATIVE_METHODS,
  DATE_FILTER_UNITS,
  DATE_FILTER_WITHIN_METHODS,
  DateFilterAbsoluteValue,
  DateFilterPeriodValue,
  DateFilterRelativeValue,
  DateFilterValue,
  FILTER_MODES,
  NUMBER_FILTER_MODES,
  NUMBER_FILTER_RELATIVE_METHODS,
  NumberFilterIntervalValue,
  NumberFilterRelativeValue,
  NumberFilterValue,
  PERIOD_METHODS,
  SELECT_FILTER_METHODS,
  SelectFilterValue,
  TIME_FILTER_ABSOLUTE_METHODS,
  TIME_FILTER_MODES,
  TableFilterTypeMap,
  TableFilterValue,
  TextFilterValue,
} from './filter';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class FilterValidator {
  static isValidFilters(
    filters: Record<string, TableFilterValue | null>,
    filterTypeMap: TableFilterTypeMap
  ): boolean {
    for (const key of Object.keys(filters)) {
      const filterValue = filters[key];

      if (!filterValue) {
        continue;
      }

      const filterType = filterTypeMap.get(key);

      switch (filterType) {
        case 'text':
          if (!this.isValidTextFilterValue(filterValue as TextFilterValue)) {
            return false;
          }
          break;
        case 'number':
          if (
            !this.isValidNumberFilterValue(filterValue as NumberFilterValue)
          ) {
            return false;
          }
          break;
        case 'date':
        case 'datetime':
          if (
            !this.isValidDateFilterValue(
              filterValue as DateFilterValue,
              filterType
            )
          ) {
            return false;
          }
          break;
        case 'time':
          if (!this.isValidTimeFilterValue(filterValue as DateFilterValue)) {
            return false;
          }
          break;
        case 'select':
          if (
            !this.isValidSelectFilterValue(filterValue as SelectFilterValue)
          ) {
            return false;
          }
          break;
        case 'amount-of-time':
          if (
            !this.isValidNumberFilterValue(filterValue as NumberFilterValue)
          ) {
            return false;
          }
          break;
        default:
          return false;
      }
    }

    return true;
  }

  private static isValidTextFilterValue(value: TextFilterValue): boolean {
    if (!FILTER_MODES.includes(value.mode)) {
      return false;
    }
    if (value.mode === 'empty') {
      return true;
    }
    if (!value.mode || !value.value) {
      return false;
    }
    return typeof value.value === 'string';
  }

  private static isValidNumber(number: number | undefined): boolean {
    return typeof number === 'number' && !isNaN(number);
  }

  private static isValidNumberFilterValue(value: NumberFilterValue): boolean {
    if (!value.mode || !value.value) {
      return false;
    }
    if (!NUMBER_FILTER_MODES.includes(value.mode)) {
      return false;
    }
    if (value.mode === 'relative') {
      const relativeValue = value.value as NumberFilterRelativeValue;
      if (
        !NUMBER_FILTER_RELATIVE_METHODS.includes(relativeValue.method) ||
        !this.isValidNumber(relativeValue.number)
      ) {
        return false;
      }
    } else if (value.mode === 'range') {
      const intervalValue = value.value as NumberFilterIntervalValue;
      if (
        !PERIOD_METHODS.includes(intervalValue.method) ||
        !this.isValidNumber(intervalValue.number1) ||
        !this.isValidNumber(intervalValue.number2)
      ) {
        return false;
      }
    }
    return true;
  }

  private static isValidDate(value: any): boolean {
    return (
      isDate(value) || (typeof value === 'string' && isValid(parseISO(value)))
    );
  }

  private static isValidTime(time: any): boolean {
    return !time || this.isValidTimeFormat(time);
  }

  private static isValidTimeFormat(time: any): boolean {
    return typeof time === 'string' && /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
  }

  private static isValidWithin(within: any, isDateTime: boolean): boolean {
    const validUnits = !isDateTime ? DATE_FILTER_UNITS : DATETIME_FILTER_UNITS;

    return (
      within &&
      this.isValidNumber(within.value) &&
      validUnits.includes(within.unit)
    );
  }

  private static isValidAbsoluteDateFilterValue(
    absoluteValue: DateFilterAbsoluteValue,
    isDateTime: boolean
  ): boolean {
    if (!this.isValidDate(absoluteValue.value)) {
      return false;
    }

    if (isDateTime) {
      if (!this.isValidTime(absoluteValue.time)) {
        return false;
      }
    } else {
      if (absoluteValue.time) {
        return false;
      }
    }

    if (!DATE_FILTER_ABSOLUTE_METHODS.includes(absoluteValue.method)) {
      return false;
    }

    if (isOneOf(DATE_FILTER_WITHIN_METHODS, absoluteValue.method)) {
      return this.isValidWithin(absoluteValue.within, isDateTime);
    }

    return true;
  }

  private static isValidAbsoluteTimeFilterValue(
    absoluteValue: DateFilterAbsoluteValue
  ): boolean {
    return (
      this.isValidTimeFormat(absoluteValue.value) &&
      isOneOf(TIME_FILTER_ABSOLUTE_METHODS, absoluteValue.method)
    );
  }

  private static isValidPeriodDateFilterValue(
    periodValue: DateFilterPeriodValue,
    isDateTime: boolean
  ): boolean {
    if (
      !this.isValidDate(periodValue.value1) ||
      !this.isValidDate(periodValue.value2)
    ) {
      return false;
    }

    if (isDateTime) {
      if (
        !this.isValidTime(periodValue.time1) ||
        !this.isValidTime(periodValue.time2)
      ) {
        return false;
      }
    } else {
      if (periodValue.time1 || periodValue.time2) {
        return false;
      }
    }

    return PERIOD_METHODS.includes(periodValue.method);
  }

  private static isValidPeriodTimeFilterValue(
    periodValue: DateFilterPeriodValue
  ): boolean {
    return (
      this.isValidTimeFormat(periodValue.value1) &&
      this.isValidTimeFormat(periodValue.value2) &&
      PERIOD_METHODS.includes(periodValue.method)
    );
  }

  private static isValidRelativeDateFilterValue(
    relativeValue: DateFilterRelativeValue,
    isDateTime: boolean
  ): boolean {
    if (!this.isValidNumber(relativeValue.value)) {
      return false;
    }

    const validUnits = !isDateTime ? DATE_FILTER_UNITS : DATETIME_FILTER_UNITS;

    return (
      isOneOf(validUnits, relativeValue.unit) &&
      DATE_FILTER_RELATIVE_METHODS.includes(relativeValue.method)
    );
  }

  static isValidDateFilterValue(
    value: DateFilterValue,
    type: 'date' | 'datetime'
  ): boolean {
    if (!value.mode || !value.value) {
      return false;
    }
    if (!DATE_FILTER_MODES.includes(value.mode)) {
      return false;
    }

    const isDateTime = type === 'datetime';

    if (value.mode === 'absolute') {
      return this.isValidAbsoluteDateFilterValue(
        value.value as DateFilterAbsoluteValue,
        isDateTime
      );
    } else if (value.mode === 'period') {
      return this.isValidPeriodDateFilterValue(
        value.value as DateFilterPeriodValue,
        isDateTime
      );
    } else if (value.mode === 'relative') {
      return this.isValidRelativeDateFilterValue(
        value.value as DateFilterRelativeValue,
        isDateTime
      );
    }
    return true;
  }

  private static isValidTimeFilterValue(value: DateFilterValue): boolean {
    if (!value.mode || !value.value) {
      return false;
    }
    if (!isOneOf(TIME_FILTER_MODES, value.mode)) {
      return false;
    }
    if (value.mode === 'absolute') {
      return this.isValidAbsoluteTimeFilterValue(
        value.value as DateFilterAbsoluteValue
      );
    } else if (value.mode === 'period') {
      return this.isValidPeriodTimeFilterValue(
        value.value as DateFilterPeriodValue
      );
    }
    return true;
  }

  private static isValidSelectFilterValue(value: SelectFilterValue): boolean {
    if (!FILTER_MODES.includes(value.mode)) {
      return false;
    }
    if (value.mode === 'empty') {
      return true;
    }
    if (!value.mode || !value.method || !value.value) {
      return false;
    }
    if (!SELECT_FILTER_METHODS.includes(value.method)) {
      return false;
    }
    if (!Array.isArray(value.value)) {
      return false;
    }
    return value.value.every(
      (item) => typeof item === 'string' || typeof item === 'number'
    );
  }
}
